import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import toast from '../components/common/toast';
import { returnService } from '../services/api';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'd MMM yyyy');
};

export function ReturnDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const fetchReturnDetails = async (returnId) => {
    try {
      setLoading(true);
      setError('');
      const res = await returnService.getReturnTransactionById(returnId);
      setData(res?.data || res || null);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load return details');
      toast.error(err?.message || 'Failed to load return details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchReturnDetails(id);
  }, [id]);

  const header = useMemo(() => {
    if (!data) return null;
    return {
      returnNumber: data.transactionId || data.returnNumber || data._id || '—',
      invoiceNumber: data.invoiceNumber || '—',
      returnDate: fmtDate(data.invoiceDate || data.returnDate || data.createdAt),
      reason: data.returnReason || '—',
    };
  }, [data]);

  const productLabel = useMemo(() => {
    const p = data?.productId;
    if (!p) return '—';
    if (typeof p === 'object') {
      const sku = p.sku ?? p.skuName ?? '—';
      const name = p.description ?? '';
      return name ? `${sku} / ${name}` : sku;
    }
    return String(p);
  }, [data]);

  const batches = useMemo(() => {
    const arr = data?.batches;
    return Array.isArray(arr) ? arr : [];
  }, [data]);

  return (
    <div className="page-container" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <div className="mb-6">
        <div className="rounded-[28px] border border-gray-100 bg-white px-6 py-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mb-2 inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                ← Back
              </button>
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                Return Details
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {loading ? 'Loading…' : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[18px] border border-[#EEF1F5] bg-white shadow-sm p-6">
        {error && !loading && (
          <div className="py-6 text-center">
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <button
              type="button"
              onClick={() => id && fetchReturnDetails(id)}
              className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
            >
              Retry
            </button>
          </div>
        )}

        {loading && (
          <div className="py-10 text-center text-gray-500 text-sm">Loading...</div>
        )}

        {!loading && !error && data && header && (
          <>
            <div className="mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm text-gray-900">
                <div><span className="text-gray-500">Return Number:</span> {header.returnNumber}</div>
                <div><span className="text-gray-500">Invoice Number:</span> {header.invoiceNumber}</div>
                <div><span className="text-gray-500">Return Date:</span> {header.returnDate}</div>
                <div><span className="text-gray-500">Reason:</span> {header.reason}</div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#F6F7FA]">
                    <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">Product</th>
                    <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Batch</th>
                    <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Mfg</th>
                    <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Exp</th>
                    <th className="text-right font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">Returned Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.length > 0 ? (
                    batches.map((b, i) => (
                      <tr key={i} className="border-t border-[#EEF1F5]">
                        <td className="px-3 py-2 text-[#101828]">{productLabel}</td>
                        <td className="px-3 py-2 text-[#101828]">{b.batchNumber ?? '—'}</td>
                        <td className="px-3 py-2 text-[#101828]">{fmtDate(b.manufacturingDate ?? b.mfgDate)}</td>
                        <td className="px-3 py-2 text-[#101828]">{fmtDate(b.expiryDate ?? b.expDate)}</td>
                        <td className="px-3 py-2 text-right text-[#101828]">{b.quantity ?? '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                        No batches
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

